<template>
  <div :class="brandClasses.wrapper" data-test-id="form-sign-in">
    <vf-notification
      v-if="notification.message"
      class="mb-4"
      :dismissible="false"
      :type="notification.type"
      @close="notification.message = ''"
    >
      <base-interpolator :content="notification.message">
        <template #reset="{ args: [message] }">
          <base-link class="underlined" target="_blank" to="/forgot-password">
            {{ message }}
          </base-link>
        </template>
        <template #contact="{ args: [message] }">
          <base-link class="underlined" target="_blank" to="/help/contact-us">
            {{ message }}
          </base-link>
        </template>
      </base-interpolator>
    </vf-notification>
    <template v-if="!compact">
      <h2 v-if="!insideDialog" :class="[brandClasses.heading, variant && brandClasses.variant?.[variant]?.heading]">
        {{ $t.signInTitle }}
      </h2>
      <vf-form-hint class="mb-2" :variant>
        {{ $t.requiredField }}
      </vf-form-hint>
    </template>
    <base-form :class="brandClasses.form" :form @submit="submit">
      <vf-form-field
        v-slot="{ attrs }"
        :hint="disableEmail ? undefined : $t.emailFormatHint"
        name="email"
        :rule="[validateRequired($t.email), validateEmail(), validateMaxLength(100)]"
        :variant
      >
        <input-email v-model="form.email" v-bind="attrs" :disabled="disableEmail" :variant @change="handleEmailChange" />
      </vf-form-field>
      <vf-form-field name="password" :rule="validateRequired($t.password)" :variant v-slot="{ attrs }">
        <input-password v-model="form.password" v-bind="attrs" required :variant />
      </vf-form-field>
      <slot name="forgot-password">
        <div :class="[brandClasses.forgotPassword, variant && brandClasses.variant?.[variant]?.forgotPassword]">
          <vf-link
            data-test-id="form-sign-in-link-forgot-password"
            :to="insideDialog ? undefined : '/forgot-password'"
            @click="handleForgotPassword"
          >
            {{ $t.forgotPassword }}
          </vf-link>
        </div>
      </slot>
      <slot name="submit-button">
        <vf-button :class="brandClasses.submitButton" data-test-id="form-sign-in-submit" :loading :size="buttonSize" type="submit">
          {{ $t.signInSubmit }}
        </vf-button>
      </slot>
      <lazy-form-sign-in-social-login v-if="$feature.configSocialLogin.length" :form-location />
      <div v-if="$feature.allowNewSignUp && showSignUp" :class="[brandClasses.signUp, variant && brandClasses.variant?.[variant]?.createAccount]">
        <base-interpolator :content="$t.notAMember">
          <template #createAccount>
            <vf-link class="ml-1" @click="openDialogSignUp">
              {{ $t.createAccount }}
            </vf-link>
          </template>
        </base-interpolator>
      </div>
    </base-form>
    <vf-button
      v-if="$feature.showSeparateSignupCtaInSigninModal && insideDialog && !disableEmail"
      class="mt-4 <md:full"
      type="submit"
      variant="tertiary"
      @click="openDialogSignUp"
    >
      {{ $t.joinLoyaltyCta }}
    </vf-button>
    <cms-section v-if="eSpot" lazy-media :section="eSpot" />
  </div>
</template>

<script lang="ts" setup>
import type { ContentPagePageItems } from '#root/api/clients/content/data-contracts'
import type { FormLocation } from '#types/gtm'
import type { BaseNotification } from '#types/notification'

const {
  compact,
  disableEmail,
  email = '',
  eSpot,
  formLocation = 'modal:single:sign-in:none',
  insideDialog,
  preventDialog,
  isSignInToBuy,
  showSignUp,
  variant
} = defineProps<{
  compact?: boolean
  disableEmail?: boolean
  email?: string
  eSpot?: ContentPagePageItems
  formLocation?: FormLocation
  insideDialog?: boolean
  preventDialog?: boolean
  isSignInToBuy?: boolean
  showSignUp?: boolean
  variant?: 'inverse'
}>()

const emit = defineEmits<{
  forgotPassword: []
  signUp: []
  submit: []
  close: []
}>()

const { brandClasses, buttonSize } = useAppConfig().components.form.signIn
const { login, postLoginActions } = useAuthStore()
const { getSections } = useCms()
const { getUserEventData } = useAnalytics()
const { DialogForgotPassword, DialogSignUp } = useDialogsStore()
const { validateEmail, validateMaxLength, validateRequired } = useLocalizedValidators()
const { $gtm, $t, $optimizely } = useNuxtApp()
const user = useUserStore()
const recaptcha = useRecaptcha()
const form = reactive({
  email,
  password: ''
})

const notification = reactive<BaseNotification>({
  message: '',
  type: ''
})

const loading = ref(false)

const handleForgotPassword = () => {
  if (insideDialog) {
    emit('forgotPassword')
    if (!preventDialog) DialogForgotPassword.open({ formLocation })
  }
}

const handleEmailChange = () => {
  $gtm.push('authForm.onEmail', 'Login', formLocation)
}

const submit = async () => {
  try {
    notification.message = '' // remove the error message, if any
    loading.value = true

    $gtm.push('authForm.onLoginSubmit', formLocation, false)

    const token = await recaptcha.execute('login')
    await login(form.email, form.password, token)

    notification.message = $t.youHaveSuccessfullyLoggedIn
    notification.type = 'success'

    await user.get()
    await postLoginActions()

    emit('submit')
    const userEventData = await getUserEventData()
    $gtm.push('user.onLoadUserData', userEventData)
    $gtm.push('authForm.onLoginConfirmed', formLocation, 'Standard', false)
    $optimizely?.dataReady(userEventData)
  }
  catch (err) {
    assertApiError(err)
    notification.message = err.message
    notification.type = 'error'
  }
  finally {
    loading.value = false
  }
}

const openDialogSignUp = async () => {
  emit('signUp')
  if (!preventDialog) {
    const signUpEspots = (await getSections.signUpModal()).data.value?.content.sectionsMap
    await DialogSignUp.keep().open(
      {
        formLocation,
        eSpot: signUpEspots?.['below-sign-up'],
        isSignInToBuy
      },
      { size: signUpEspots?.['below-sign-up'] ? 'md' : 'xs' }
    )
    emit('close')
  }
}

onMounted(() => {
  $gtm.queueEvent('authForm.onImpression', 'Login', formLocation)
  form.email.length > 0 && (document.querySelector('[type="password"]') as HTMLElement)?.focus()
})
</script>
